import { RiCheckboxCircleFill, RiAlertFill, RiTimeLine } from '@remixicon/react';
import type { TimeSlot } from '../../types';
import { useLang } from '../../i18n/translations';
import { cn } from '../../lib/utils';

interface Props {
  dayLabel: string;
  slots: TimeSlot[];
  date: string;
}

type T = ReturnType<typeof useLang>['t'];

interface Verdict {
  tone: 'go' | 'wait' | 'avoid';
  headline: string;
  detail: string;
}

function slotMinutes(time: string): number {
  const m = time.match(/^(\d{1,2}):(\d{2})/);
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 0;
}

function buildVerdict(slots: TimeSlot[], date: string, t: T): Verdict {
  const v = t.verdict;
  if (slots.length === 0) {
    return { tone: 'avoid', headline: v.noData, detail: v.noDataSub };
  }

  const isToday = date === new Date().toISOString().split('T')[0];
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const pool = isToday ? slots.filter((s) => slotMinutes(s.time) >= nowMin - 15) : slots;

  if (pool.length === 0) {
    return { tone: 'avoid', headline: v.noMore, detail: v.noMoreSub };
  }

  const best = [...pool].sort(
    (a, b) => a.crowd_score - b.crowd_score || a.time.localeCompare(b.time),
  )[0];

  const dayName = t.search.dayFull[new Date(date + 'T00:00:00').getDay()];

  if (best.crowd_level === 'low') {
    return {
      tone: 'go',
      headline: isToday ? v.goToday(best.time) : v.goFuture(dayName, best.time),
      detail: v.goSub(best.crowd_score, isToday),
    };
  }
  if (best.crowd_level === 'medium') {
    return {
      tone: 'wait',
      headline: v.waitHead(best.time),
      detail: v.waitSub(best.crowd_score, isToday),
    };
  }
  return {
    tone: 'avoid',
    headline: v.avoidHead(isToday),
    detail: v.avoidSub(best.time, best.crowd_score),
  };
}

const PALETTE = {
  go: {
    wrap: 'bg-brand-subtle border-brand-border',
    iconBg: 'bg-brand',
    tag: 'text-brand-dark',
    Icon: RiCheckboxCircleFill,
  },
  wait: {
    wrap: 'bg-warn-light border-warn-border',
    iconBg: 'bg-warn',
    tag: 'text-warn-dark',
    Icon: RiTimeLine,
  },
  avoid: {
    wrap: 'bg-danger-light border-danger-border',
    iconBg: 'bg-danger',
    tag: 'text-danger-dark',
    Icon: RiAlertFill,
  },
} as const;

export function RecommendationBanner({ slots, date }: Props) {
  const { t } = useLang();
  const verdict = buildVerdict(slots, date, t);
  const p = PALETTE[verdict.tone];

  return (
    <div className={cn('rounded-2xl p-4 flex items-start gap-3 border', p.wrap)}>
      <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', p.iconBg)}>
        <p.Icon className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('text-[10px] font-display font-semibold uppercase tracking-wider mb-0.5', p.tag)}>
          {t.predict.tag}
        </p>
        <p className="text-[14px] font-semibold font-display text-ink leading-snug">
          {verdict.headline}
        </p>
        <p className="text-[12px] font-display text-ink-muted mt-1 leading-snug">
          {verdict.detail}
        </p>
      </div>
    </div>
  );
}
